import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'

// 本番環境: VITE_API_URLを使用、開発環境: /apiプロキシを使用
const apiUrl = import.meta.env.VITE_API_URL || '/api'

// Axiosインスタンス作成
export const apiClient = axios.create({
  baseURL: apiUrl,
})

/**
 * 署名付きURLを素のfetch()で使えるURLに変換
 *
 * バックエンドは "/api/e/{id}/stream?sig=..." 形式の相対パスを返す。
 * 開発環境ではViteプロキシが /api → バックエンドへ転送するのでそのまま動く。
 * 本番環境ではプロキシがないため、VITE_API_URLを使って絶対URLに変換する。
 */
export const resolveSignedUrl = (url: string): string => {
  // 開発環境（/apiプロキシ）ではそのまま返す
  if (apiUrl === '/api') {
    return url
  }
  // 本番環境: /api/... → {VITE_API_URL}/... に変換
  if (url.startsWith('/api/')) {
    return `${apiUrl}${url.slice(4)}`
  }
  if (url === '/api') {
    return apiUrl
  }
  return url
}

/**
 * 署名付きURLを、Viteプロキシをバイパスしてバックエンドに直接アクセスできる絶対URLに変換
 *
 * resolveSignedUrlが既に絶対URLを返している場合はそのまま返す。
 * 相対パス（/api/...）の場合はVITE_API_URLで絶対URLに変換する。
 * 動画ストリーミングや画像の直接取得など、プロキシ経由では問題がある場合に使用。
 */
export const resolveDirectUrl = (url: string): string => {
  // 既に絶対URLならそのまま返す
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url
  }
  // 相対パス: /api/... → {VITE_API_URL}/... に変換
  const cleanPath = url.startsWith('/api/') ? url.slice(4) : url.startsWith('/api') ? url.slice(4) : url
  if (apiUrl !== '/api') {
    return `${apiUrl}${cleanPath}`
  }
  // 開発環境: そのまま返す（Viteプロキシが転送）
  return url
}

// トークン取得関数を保持
let getTokenFn: (() => Promise<string | null>) | null = null

// トークン強制リフレッシュ関数を保持
let forceRefreshTokenFn: (() => Promise<string | null>) | null = null

/**
 * この端末/タブを一意に識別する ID。
 * SSE 経由で他端末の変更通知を受け取る際、自端末発の通知を無視するために利用する。
 * 全 API リクエストで `X-Client-Id` ヘッダーとして送信される。
 */
export const CLIENT_ID: string =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `c-${Date.now()}-${Math.random().toString(36).slice(2)}`

/**
 * 現在のJWTトークンを取得する
 * apiClient以外（素のfetchなど）で認証ヘッダーが必要な場合に使用
 */
export const getAuthToken = async (): Promise<string | null> => {
  if (!getTokenFn) return null
  return getTokenFn()
}

// JWTのペイロードをデコード（検証なし）
const decodeJwtPayload = (token: string): { exp?: number; iat?: number } | null => {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const payload = JSON.parse(atob(parts[1]))
    return payload
  } catch {
    return null
  }
}

/**
 * 認証ヘッダー付きのfetch関数を返す
 * Readium HttpFetcher等、素のfetchインターフェースが必要な箇所で使用
 * 401時はトークンをリフレッシュして1度だけリトライする
 */
export const createAuthenticatedFetch = (): typeof fetch => {
  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const token = await getAuthToken()

    const headers = new Headers(init?.headers)
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }
    const response = await fetch(input, { ...init, headers, cache: 'no-store' })

    // 401かつリフレッシュ関数がある場合はトークンを再取得してリトライ
    if (response.status === 401 && forceRefreshTokenFn) {
      try {
        const newToken = await forceRefreshTokenFn()
        if (newToken) {
          const retryHeaders = new Headers(init?.headers)
          retryHeaders.set('Authorization', `Bearer ${newToken}`)
          return await fetch(input, { ...init, headers: retryHeaders, cache: 'no-store' })
        }
      } catch {
        // トークンリフレッシュ失敗時は元のレスポンスを返す
      }
    }

    return response
  }
}

// トークン取得関数を設定
export const setTokenGetter = (fn: () => Promise<string | null>, forceRefreshFn?: () => Promise<string | null>) => {
  getTokenFn = fn
  forceRefreshTokenFn = forceRefreshFn ?? null
}

// 認証不要なエンドポイント（パブリックAPI）
const PUBLIC_ENDPOINTS = ['/health', '/public/']

const isPublicEndpoint = (url: string | undefined): boolean => {
  if (!url) return false
  return PUBLIC_ENDPOINTS.some((ep) => url.startsWith(ep) || url.includes(ep))
}

/** 通常の CRUD 用。電波が悪くレスポンスが返らないまま画面が固まるのを防ぐ */
const DEFAULT_TIMEOUT_MS = 60_000
/** AI 生成や外部 API 連携など、分単位で待つことがあるエンドポイント用 */
const LONG_TIMEOUT_MS = 5 * 60_000

/** LONG_TIMEOUT_MS を適用するパス。AI 生成・外部サービス問い合わせが該当する */
const LONG_RUNNING_PATTERNS = [
  /(extract|generate|suggest|optimize|advice|analyze|regenerate|parse|poster|story|evaluate|ai-propose)/,
  /(auto-schedule|visit-suggestions|re-geocode|recalc-counts|create-from-search|split-days)/,
  /^\/(translate|grammar|stamp-rally-imports|calil)\b/,
  /^\/import\/amazon-metadata\b/,
  /google/,
]

/**
 * リクエストごとのタイムアウトを決める。
 *
 * ストリーミング(SSE)・バイナリ転送・アップロードは完了までの時間が読めないため無制限にする。
 * 呼び出し側が明示的に timeout を指定していればそれを優先する。
 */
export const resolveTimeout = (config: InternalAxiosRequestConfig): number => {
  if (config.timeout) return config.timeout
  if (config.onDownloadProgress || config.onUploadProgress) return 0
  if (config.responseType === 'blob') return 0
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) return 0
  if (typeof Blob !== 'undefined' && config.data instanceof Blob) return 0
  const url = config.url ?? ''
  if (LONG_RUNNING_PATTERNS.some((pattern) => pattern.test(url))) return LONG_TIMEOUT_MS
  return DEFAULT_TIMEOUT_MS
}

// リクエストインターセプター
apiClient.interceptors.request.use(
  async (config) => {
    // 全リクエストに端末識別 ID を付与（SSE の自端末発イベント判別用）
    config.headers['X-Client-Id'] = CLIENT_ID
    config.timeout = resolveTimeout(config)

    // パブリックエンドポイントは認証不要
    if (isPublicEndpoint(config.url)) {
      return config
    }

    // オフライン時はリクエストをキャンセル（サイレント）
    if (!navigator.onLine) {
      const controller = new AbortController()
      controller.abort()
      config.signal = controller.signal
      return config
    }

    if (!getTokenFn) {
      console.error('[API Client] No token getter set - blocking request')
      return Promise.reject(new Error('認証が設定されていません'))
    }

    try {
      const token = await getTokenFn()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      } else {
        console.error('[API Client] Token is null - blocking request')
        return Promise.reject(new Error('認証トークンが取得できません'))
      }
    } catch (error) {
      console.error('[API Client] Failed to get token:', error)
      return Promise.reject(new Error('認証トークンの取得に失敗しました'))
    }

    return config
  },
  (error) => Promise.reject(error),
)

// レスポンスインターセプター（401エラー時にトークンリフレッシュしてリトライ）
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // 401エラーかつリトライしていない場合
    if (error.response?.status === 401 && !originalRequest._retry && forceRefreshTokenFn) {
      originalRequest._retry = true

      const oldToken = originalRequest.headers.Authorization?.toString().replace('Bearer ', '')
      const oldPayload = oldToken ? decodeJwtPayload(oldToken) : null
      const now = Math.floor(Date.now() / 1000)

      console.log('[API Client] 401 received, attempting token refresh...')
      console.log(
        '[API Client] Old token exp:',
        oldPayload?.exp,
        'now:',
        now,
        'expired:',
        oldPayload?.exp ? oldPayload.exp - now : 'N/A',
        'seconds ago',
      )

      try {
        const newToken = await forceRefreshTokenFn()
        const newPayload = newToken ? decodeJwtPayload(newToken) : null

        console.log(
          '[API Client] New token exp:',
          newPayload?.exp,
          'now:',
          now,
          'expires in:',
          newPayload?.exp ? newPayload.exp - now : 'N/A',
          'seconds',
        )
        console.log('[API Client] Token actually changed:', oldToken !== newToken)

        if (newToken && newPayload?.exp && newPayload.exp > now) {
          console.log('[API Client] Token refreshed successfully with valid expiry, retrying...')
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          return apiClient(originalRequest)
        } else if (newToken) {
          console.error('[API Client] New token is also expired! exp:', newPayload?.exp, 'now:', now)
        } else {
          console.error('[API Client] forceRefreshTokenFn returned null')
        }
      } catch (refreshError) {
        console.error('[API Client] Token refresh failed:', refreshError)
      }
    }

    // タイムアウトは "timeout of 60000ms exceeded" のままトーストに出るので日本語に置き換える
    if (error.code === 'ECONNABORTED') {
      error.message = '通信がタイムアウトしました。電波状況を確認してもう一度お試しください'
    }

    return Promise.reject(error)
  },
)
